import { Link } from "react-router-dom";
import { Check } from "lucide-react";
import { useSEO } from "@/lib/seo";
import { useFetch } from "@/lib/content";
import { Eyebrow, Reveal, CtaButton } from "@/components/site/Motion";
import FrameworkStack from "@/components/site/FrameworkStack";
import content from "@/content/frameworks.json";

function FrameworkSection({ f, index, seriesBySlug }) {
  return (
    <Reveal className="border-t border-ash">
      <article id={f.id} data-testid={`framework-${f.id}`} className="grid gap-8 py-14 md:grid-cols-12 md:gap-10 md:py-20">
        <div className="md:col-span-3">
          <span className="font-serif text-2xl text-progress">{String(index + 1).padStart(2, "0")}</span>
          <p className="mt-3 text-sm font-bold uppercase tracking-[0.2em] text-slategray">{f.stage}</p>
        </div>
        <div className="md:col-span-5">
          <h2 className="font-serif text-3xl font-medium leading-tight tracking-tight md:text-4xl">{f.name}</h2>
          {f.expansion && <p className="mt-2 text-sm font-medium tracking-wide text-progress">{f.expansion}</p>}
          <p className="mt-6 font-serif text-xl leading-snug text-obsidian md:text-2xl">{f.headline}</p>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-obsidian/70">{f.description}</p>
        </div>
        <div className="md:col-span-4">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slategray">What it gives you</p>
          <ul className="mt-4 space-y-3">
            {f.gives.map((g) => (
              <li key={g} className="flex gap-3 text-[15px] leading-relaxed text-obsidian/80">
                <Check size={16} className="mt-1 shrink-0 text-progress" />
                {g}
              </li>
            ))}
          </ul>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-slategray">Used in</p>
          <p className="mt-2 text-sm text-obsidian/80">
            {f.used_in_label ||
              f.used_in.map((code, i) => {
                const s = seriesBySlug[code];
                return (
                  <span key={code}>
                    {i > 0 && " · "}
                    {s ? (
                      <Link to={`/services/${s.slug}`} className="font-bold text-obsidian underline-offset-4 hover:text-progress hover:underline">
                        {code} Series
                      </Link>
                    ) : (
                      `${code} Series`
                    )}
                  </span>
                );
              })}
          </p>
        </div>
      </article>
    </Reveal>
  );
}

export default function Frameworks() {
  const { data: services } = useFetch("/services");
  const seriesBySlug = Object.fromEntries((services || []).map((s) => [s.series_code, s]));
  const { intro, frameworks } = content;

  useSEO({
    title: "RenB Frameworks — Seven Frameworks, One Way of Working | RenB Solutions",
    description: "The seven proprietary frameworks behind every RenB engagement: B.A.S.E., G.R.O.W.T.H. Engine, C.O.R.E. Excellence System, D.A.S.H. Intelligence, 3T HRD, ELPC Sales Model and the CEO Coaching Framework.",
    path: "/frameworks",
  });

  return (
    <div data-testid="frameworks-page">
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-20 md:pt-32 lg:grid-cols-12 lg:items-center lg:gap-8">
          <Reveal className="lg:col-span-6">
            <Eyebrow>{intro.eyebrow}</Eyebrow>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              Seven frameworks. One way of working<span className="text-progress">.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-obsidian/70 md:text-lg">{intro.lead}</p>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-obsidian/70">{intro.note}</p>
          </Reveal>
          <div className="hidden lg:col-span-5 lg:col-start-8 lg:block">
            <FrameworkStack frameworks={frameworks} />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-8 md:px-10 md:py-12">
        {frameworks.map((f, i) => (
          <FrameworkSection key={f.id} f={f} index={i} seriesBySlug={seriesBySlug} />
        ))}
      </section>

      <section className="border-t border-ash bg-obsidian text-paper">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-7">
            <Eyebrow light>Under NDA</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight md:text-5xl">{intro.nda}</h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-paper/70">
              Start with a 15-minute discovery call. We will tell you which of the seven your business needs — and which it doesn&apos;t.
            </p>
          </div>
          <div className="flex items-end md:col-span-4 md:col-start-9 md:justify-end">
            <CtaButton href="/book" testid="frameworks-cta">
              Book a discovery call
            </CtaButton>
          </div>
        </div>
      </section>
    </div>
  );
}
